import { useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, MapPin, Lock, Plus, Loader2, Tag, Sparkles, Bell, Check } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { toast } from 'sonner';

// Marketplace is built but intentionally locked as a "coming soon" teaser
// until after the Play Store launch. Flip to false to enable it fully.
export const MARKETPLACE_LOCKED = true;

const TEASER_LISTINGS = [
    { title: 'Heritage tomato seeds — 5 varieties', price: '25', city: 'Aarhus', cat: 'seeds' },
    { title: 'Self-watering raised bed kit', price: '350', city: 'København', cat: 'tools' },
    { title: 'Organic worm compost · 10 L', price: '60', city: 'Odense', cat: 'compost' },
    { title: 'Apple tree saplings (bare root)', price: '120', city: 'Aalborg', cat: 'saplings' },
];

const CATEGORIES = [
    { value: 'seeds', label: 'Seeds' },
    { value: 'saplings', label: 'Saplings' },
    { value: 'tools', label: 'Tools' },
    { value: 'compost', label: 'Compost / soil' },
    { value: 'other', label: 'Other' },
];

function MarketplaceLockedTeaser() {
    const navigate = useNavigate();
    const { t } = useI18n();
    return (
        <div className="px-5 pt-10 pb-32 max-w-3xl mx-auto relative" data-testid="marketplace-locked">
            <button onClick={() => navigate(-1)} data-testid="marketplace-back" className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> {t('premium.back')}</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('market.eyebrow')}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-6">{t('market.heading')}</h1>

            <div className="relative">
                {/* Blurred teaser content behind the glass */}
                <div className="space-y-3 select-none pointer-events-none blur-[6px] opacity-70" aria-hidden="true">
                    {TEASER_LISTINGS.map((l) => (
                        <div key={l.title} className="w-full bg-brand-card border border-brand-border rounded-3xl p-4 text-left">
                            <div className="flex items-baseline justify-between gap-3 mb-1">
                                <h3 className="font-serif text-lg text-brand-ink">{l.title}</h3>
                                <span className="text-brand-green font-semibold text-sm whitespace-nowrap">{l.price} DKK</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-brand-ink3 mt-2">
                                <span className="inline-flex items-center gap-1"><Tag size={11} /> {l.cat}</span>
                                <span className="inline-flex items-center gap-1"><MapPin size={11} /> {l.city}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Glass "coming soon" overlay */}
                <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="backdrop-blur-md bg-brand-bg/70 border border-brand-border rounded-3xl shadow-[0_8px_40px_-16px_rgba(27,38,28,0.35)] p-7 max-w-sm text-center" data-testid="marketplace-coming-soon">
                        <div className="w-14 h-14 rounded-2xl bg-brand-green/12 text-brand-green flex items-center justify-center mx-auto mb-4">
                            <Lock size={26} strokeWidth={1.7} />
                        </div>
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-brand-ochre/20 text-brand-ochre px-3 py-1 rounded-full">
                            <Sparkles size={11} /> {t('market.coming_soon')}
                        </span>
                        <h2 className="font-serif text-2xl text-brand-ink mt-3">{t('market.teaser_title')}</h2>
                        <p className="text-sm text-brand-ink2 mt-2 leading-relaxed">
                            {t('market.teaser_body')}
                        </p>
                        <MarketplaceNotify />
                        <button
                            onClick={() => navigate('/dashboard')}
                            data-testid="marketplace-back-dashboard"
                            className="mt-3 text-sm text-brand-ink3 hover:text-brand-ink underline"
                        >
                            {t('market.back_garden')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MarketplaceNotify() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [email, setEmail] = useState(user?.email && !String(user.email).endsWith('@guest.local') ? user.email : '');
    const [wantPush, setWantPush] = useState(true);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            toast.error(t('market.invalid_email'));
            return;
        }
        setBusy(true);
        try {
            await api.post('/marketplace/notify', { email, want_push: wantPush });
            setDone(true);
            toast.success(t('market.on_list'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('market.signup_fail'));
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <div data-testid="notify-done" className="mt-5 bg-brand-green/10 border border-brand-green/30 rounded-2xl p-3 text-sm text-brand-ink2 inline-flex items-center gap-2">
                <Check size={16} className="text-brand-green" /> {t('market.notify_done')}
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="notify-form" className="mt-5 text-left">
            <input
                data-testid="notify-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('market.your_email')}
                required
                className="w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2.5 text-sm text-brand-ink placeholder:text-brand-ink3 focus:outline-none focus:border-brand-green"
            />
            <label className="flex items-center gap-2 mt-2 text-xs text-brand-ink2 cursor-pointer select-none">
                <input data-testid="notify-push" type="checkbox" checked={wantPush} onChange={(e) => setWantPush(e.target.checked)} className="accent-[color:var(--brand-green)]" />
                {t('market.also_phone')}
            </label>
            <button
                data-testid="notify-submit"
                type="submit"
                disabled={busy}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-brand-green text-white py-2.5 rounded-full text-sm font-medium hover:bg-brand-greenDark disabled:opacity-50 transition-colors"
            >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />}
                {busy ? t('market.signing_up') : t('market.notify_me')}
            </button>
        </form>
    );
}

export function MarketplaceFeed() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        if (MARKETPLACE_LOCKED) return;
        (async () => {
            try {
                const params = filter !== 'all' ? `?category=${filter}` : '';
                const r = await api.get(`/marketplace/listings${params}`);
                setListings(r.data || []);
            } catch {
                setListings([]);
            } finally { setLoading(false); }
        })();
    }, [filter]);

    if (MARKETPLACE_LOCKED) return <MarketplaceLockedTeaser />;

    return (
        <div className="px-5 pt-10 pb-32 max-w-3xl mx-auto" data-testid="marketplace-feed">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Marketplace</p>
                {user && (
                    <button
                        onClick={() => navigate('/marketplace/new')}
                        data-testid="btn-new-listing"
                        className="inline-flex items-center gap-1 bg-brand-green hover:bg-brand-greenDark text-white rounded-full px-3 py-1.5 text-xs font-medium"
                    >
                        <Plus size={12} /> Sell
                    </button>
                )}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-6">Seeds, saplings & gardening gear</h1>

            <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1" data-testid="cat-filter">
                {[{ value: 'all', label: 'All' }, ...CATEGORIES].map(c => (
                    <button
                        key={c.value}
                        onClick={() => setFilter(c.value)}
                        data-testid={`filter-${c.value}`}
                        className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filter === c.value ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin inline" size={24} /></div>
            ) : listings.length === 0 ? (
                <p className="text-brand-ink3 text-center py-16" data-testid="empty-marketplace">No listings yet. {user ? <button onClick={() => navigate('/marketplace/new')} className="underline">Be the first to sell.</button> : 'Sign in to list yours.'}</p>
            ) : (
                <div className="space-y-3 stagger">
                    {listings.map(l => (
                        <button
                            key={l.id}
                            onClick={() => navigate(`/marketplace/${l.id}`)}
                            data-testid={`listing-${l.id}`}
                            className="w-full bg-brand-card border border-brand-border rounded-3xl p-4 text-left hover:bg-brand-bg2 transition-colors"
                        >
                            <div className="flex items-baseline justify-between gap-3 mb-1">
                                <h3 className="font-serif text-lg text-brand-ink">{l.title}</h3>
                                <span className="text-brand-green font-semibold text-sm whitespace-nowrap">{l.price > 0 ? `${l.price} ${l.currency}` : 'Free'}</span>
                            </div>
                            {l.description && <p className="text-sm text-brand-ink3 line-clamp-2 mb-2">{l.description}</p>}
                            <div className="flex items-center gap-3 text-xs text-brand-ink3">
                                <span className="inline-flex items-center gap-1"><Tag size={11} /> {l.category}</span>
                                {l.location_city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {l.location_city}</span>}
                                <span className="ml-auto">by {l.seller_name}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function ListingDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(false);
    const buy = async () => {
        if (!user) {
            navigate('/login');
            return;
        }
        if (listing.seller_id === user.user_id) {
            toast.error("You can't buy your own listing");
            return;
        }
        setBuying(true);
        try {
            const r = await api.post('/marketplace/orders', { listing_id: id, quantity: 1 });
            if (r.data?.approve_url) {
                window.location.href = r.data.approve_url;
                return;
            }
            toast.error('Could not start payment');
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Payment unavailable');
        } finally { setBuying(false); }
    };

    useEffect(() => {
        if (MARKETPLACE_LOCKED) return;
        (async () => {
            try {
                const r = await api.get(`/marketplace/listings/${id}`);
                setListing(r.data);
                const ogImg = `${window.location.origin}/api/og/marketplace/${id}.png`;
                const setMeta = (sel, attr, val) => {
                    let el = document.head.querySelector(sel);
                    if (!el) {
                        el = document.createElement('meta');
                        const m = sel.match(/\[(\w+)="(.+)"\]/);
                        if (m) el.setAttribute(m[1], m[2]);
                        document.head.appendChild(el);
                    }
                    el.setAttribute(attr, val);
                };
                document.title = `${r.data.title} — GrowDaily marketplace`;
                setMeta('meta[property="og:title"]', 'content', r.data.title);
                setMeta('meta[property="og:description"]', 'content', r.data.description || `For sale on GrowDaily — ${r.data.price} ${r.data.currency}`);
                setMeta('meta[property="og:image"]', 'content', ogImg);
                setMeta('meta[property="og:url"]', 'content', window.location.href);
                setMeta('meta[name="twitter:image"]', 'content', ogImg);
            } catch {
                toast.error('Listing not found');
            } finally { setLoading(false); }
        })();
    }, [id]);

    if (MARKETPLACE_LOCKED) return <Navigate to="/marketplace" replace />;
    if (loading) return <div className="text-center py-24"><Loader2 className="animate-spin inline" /></div>;
    if (!listing) return <div className="text-center py-24 text-brand-ink3">Not found</div>;

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="listing-detail">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{listing.category}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-3">{listing.title}</h1>
            <div className="flex items-baseline gap-3 mb-4">
                <span className="text-2xl font-semibold text-brand-green">{listing.price > 0 ? `${listing.price} ${listing.currency}` : 'Free'}</span>
                {listing.quantity && listing.quantity > 1 && <span className="text-sm text-brand-ink3">× {listing.quantity}</span>}
            </div>
            {listing.description && <p className="text-brand-ink mb-6 whitespace-pre-line">{listing.description}</p>}
            <div className="bg-brand-card border border-brand-border rounded-3xl p-5 mb-4 space-y-2">
                <div className="flex items-center gap-2 text-sm"><span className="text-brand-ink3 w-24">Seller</span><span className="text-brand-ink">{listing.seller_name}</span></div>
                {listing.location_city && <div className="flex items-center gap-2 text-sm"><span className="text-brand-ink3 w-24">Location</span><span className="text-brand-ink">{listing.location_city}</span></div>}
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-brand-ink3 w-24">Contact</span>
                    {listing.contact_locked ? (
                        <button onClick={() => navigate('/login')} className="inline-flex items-center gap-1 text-brand-green underline" data-testid="btn-unlock-contact">
                            <Lock size={12} /> Sign in to see contact
                        </button>
                    ) : (
                        <span className="text-brand-ink font-mono text-xs">{listing.contact_value}</span>
                    )}
                </div>
            </div>
            <p className="text-xs text-brand-ink3">GrowDaily doesn't process payment. Coordinate directly with the seller — meet in a public place when handing over saplings or tools.</p>

            {listing.price > 0 && listing.status === 'active' && listing.seller_id !== user?.user_id && (
                <button
                    onClick={buy}
                    disabled={buying}
                    data-testid="btn-buy-listing"
                    className="mt-6 w-full bg-brand-green hover:bg-brand-greenDark text-white py-4 rounded-full font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                    {buying ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                    {buying ? 'Starting checkout…' : `Buy now — ${listing.price} ${listing.currency}`}
                </button>
            )}
            {listing.price > 0 && (
                <p className="mt-2 text-[11px] text-brand-ink3 text-center">
                    Pay securely with PayPal, Apple Pay or Google Pay
                </p>
            )}
        </div>
    );
}

export function NewListing() {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: '', description: '', category: 'seeds', price: 0, currency: 'DKK',
        quantity: 1, location_city: '', contact_method: 'email', contact_value: '',
    });
    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error('Title required');
        setBusy(true);
        try {
            const r = await api.post('/marketplace/listings', form);
            toast.success('Listed!');
            navigate(`/marketplace/${r.data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not create listing');
        } finally { setBusy(false); }
    };
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
    if (MARKETPLACE_LOCKED) return <Navigate to="/marketplace" replace />;
    return (
        <div className="px-5 pt-10 pb-32 max-w-xl mx-auto" data-testid="new-listing">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">List for sale</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-6 inline-flex items-center gap-2"><ShoppingBag size={28} /> New listing</h1>
            <form onSubmit={submit} className="space-y-4">
                <label className="block">
                    <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Title</span>
                    <input data-testid="input-title" value={form.title} onChange={set('title')} required maxLength={120} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" />
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Description</span>
                    <textarea data-testid="input-desc" value={form.description} onChange={set('description')} rows="3" className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3 resize-none" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Category</span>
                        <select data-testid="select-category" value={form.category} onChange={set('category')} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3">
                            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Price (DKK)</span>
                        <input data-testid="input-price" type="number" min="0" value={form.price} onChange={set('price')} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" />
                    </label>
                </div>
                <label className="block">
                    <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">City</span>
                    <input data-testid="input-city" value={form.location_city} onChange={set('location_city')} maxLength={60} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" />
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Contact email/phone (visible only to signed-in buyers)</span>
                    <input data-testid="input-contact" value={form.contact_value} onChange={set('contact_value')} placeholder="Defaults to your account email" className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" />
                </label>
                <button data-testid="btn-submit-listing" disabled={busy} type="submit" className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50">
                    {busy ? 'Saving…' : 'Publish listing'}
                </button>
            </form>
        </div>
    );
}
